import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { ContextMenu } from "./ContextMenu";
import { MenuOptionType } from "./models/MenuOptionType";

type ContextMenuConfig = {
	id: string;
	getOptions: (data: any) => MenuOptionType[];
};

interface ContextMenuMESFContextType {
	show: boolean;
	position: { x: number; y: number };
	options: MenuOptionType[];
	showContextMenu: (e: React.MouseEvent, data: any, configId: string) => void;
	registerConfig: (config: ContextMenuConfig) => void;
	onClickOutside: () => void;
}

const ContextMenuMESFContext = createContext<ContextMenuMESFContextType>({
	show: false,
	position: { x: 0, y: 0 },
	options: [],
	showContextMenu: () => {},
	registerConfig: () => {},
	onClickOutside: () => {},
});

export const ContextMenuMESFProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const [show, setShow] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [options, setOptions] = useState<MenuOptionType[]>([]);
	const [configs, setConfigs] = useState<{ [key: string]: ContextMenuConfig }>(
		{},
	);
	const currentDataRef = useRef<any>(null);
	const currentConfigIdRef = useRef<string | null>(null);

	const registerConfig = useCallback((config: ContextMenuConfig) => {
		setConfigs((prev) => {
			const currentConfig = prev[config.id];

			if (currentConfig?.getOptions === config.getOptions) {
				return prev;
			}

			return { ...prev, [config.id]: config };
		});
	}, []);

	const showContextMenu = useCallback(
		(e: React.MouseEvent, data: any, configId: string) => {
			e.preventDefault();
			e.stopPropagation();
			setShow(true);
			setPosition({ x: e.clientX, y: e.clientY });
			currentDataRef.current = data;
			currentConfigIdRef.current = configId;
			if (configs[configId]) {
				setOptions(configs[configId].getOptions(data));
			} else {
				console.warn(`No config found for id: ${configId}`);
				setOptions([]);
			}
		},
		[configs],
	);

	const onClickOutside = useCallback(() => {
		setShow(false);
	}, []);

	useEffect(() => {
		const handleGlobalClick = () => {
			if (show) {
				onClickOutside();
			}
		};

		document.addEventListener("click", handleGlobalClick);

		return () => {
			document.removeEventListener("click", handleGlobalClick);
		};
	}, [show]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClickOutside();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [onClickOutside]);

	const contextValue = useMemo<ContextMenuMESFContextType>(
		() => ({
			show,
			position,
			options,
			showContextMenu,
			registerConfig,
			onClickOutside,
		}),
		[onClickOutside, options, position, registerConfig, show, showContextMenu],
	);

	return (
		<ContextMenuMESFContext.Provider value={contextValue}>
			{children}
			<ContextMenu show={show} position={position} options={options} />
		</ContextMenuMESFContext.Provider>
	);
};

export const useContextMenuMESF = () => useContext(ContextMenuMESFContext);
